import { jsPDF } from "jspdf";
import moment from "moment";

import { ICompany } from "../../features/company/companySlice";
import { GenderDict, IUser } from "../../features/users/usersSlice";
import { FONT_INTER_BOLD, FONT_INTER_NORMAL, FONT_INTER_REGULAR } from "../Certificates/fontFiles";
import { getTranslations } from "../../features/translations/translationsUtils";
import { idLabelsByType, IdTypes } from "../Users/common/identification";
import { getCountryName } from "../Users/utils";
import { IEvalWithCategory } from "./Profile";


const PAGE_PADDING = 30;
const COLUMN_PADDING_X = 10;
const COLUMN_PADDING_Y = 20;
const ROUNDED_COLUMN = 10;

export const RATIO_FACTOR = 0.4;

export const drawImage = (
  id: string,
  doc: jsPDF,
  x: number,
  y: number,
) => {
  let imgCanvas = document.createElement("CANVAS") as HTMLCanvasElement;
  let businessLogo: HTMLImageElement = document.getElementById(
    id
  ) as HTMLImageElement;
  const ratio = businessLogo.height / businessLogo.width;
  // In order to get a better resolution from the svg
  // the width is dupicated.
  const width = businessLogo.width * 2;
  const height = width * ratio;
  imgCanvas.width = width;
  imgCanvas.height = height;
  let ctx = imgCanvas.getContext("2d");
  // to get the actual width that svg will use on pdf
  // we need to use the 842 that is the max width used on the HTML
  // to calculate the width
  const imgWidth = (businessLogo.width * RATIO_FACTOR) / 1.2;
  const imgHeight = imgWidth * ratio;
  ctx?.beginPath();
  ctx?.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
  ctx?.closePath();
  ctx?.clip();
  ctx?.drawImage(businessLogo, 0, 0, width, height);

  var img = imgCanvas.toDataURL("image/png", 1);

  doc.addImage(
    img,
    "PNG",
    x,
    y,
    imgWidth,
    imgHeight
  );
};

export const drawImageCircular = (
  id: string,
  doc: jsPDF,
  x: number,
  y: number,
) => {
  let imgCanvas = document.createElement("CANVAS") as HTMLCanvasElement;
  let businessLogo: HTMLImageElement = document.getElementById(
    id
  ) as HTMLImageElement;
  const ratio = businessLogo.height / businessLogo.width;
  // In order to get a better resolution from the svg
  // the width is dupicated.
  const width = businessLogo.width * 2;
  const height = width * ratio;
  imgCanvas.width = width;
  imgCanvas.height = height;
  let ctx = imgCanvas.getContext("2d");
  // to get the actual width that svg will use on pdf
  // we need to use the 842 that is the max width used on the HTML
  // to calculate the width
  const imgWidth = (businessLogo.width * RATIO_FACTOR) / 1.2;
  const imgHeight = imgWidth * ratio;
  ctx?.beginPath();
  ctx?.arc(width / 2, height / 2, width / 2, 0, Math.PI * 2);
  ctx?.closePath();
  ctx?.clip();
  ctx?.drawImage(businessLogo, 0, 0, width, height);

  var img = imgCanvas.toDataURL("image/png", 1);

  doc.addImage(
    img,
    "PNG",
    x,
    y,
    imgWidth,
    imgHeight
  );
};


export const ProfileToPDF = async (
  user: IUser,
  company: ICompany,
  groupsOfCourses: IEvalWithCategory[][],
  groupsOfLessons: IEvalWithCategory[][],
  groupsOfTopics: IEvalWithCategory[][],
) => {
  const doc = new jsPDF({
    orientation: "p",
    unit: "px",
    format: "a4",
  });

  doc.addFileToVFS("Inter.ttf", FONT_INTER_NORMAL);
  doc.addFileToVFS("InterRegular.ttf", FONT_INTER_REGULAR);
  doc.addFileToVFS("InterBold.ttf", FONT_INTER_BOLD);

  const initialY = 90;
  const pageWidth = doc.internal.pageSize.width;
  const firstColumnStartY = initialY;
  const firstColumnHeight = 335;
  const firstColumnStartX = PAGE_PADDING;
  const firstColumnInternalX = firstColumnStartX + COLUMN_PADDING_X;
  const firstColumnWidth = pageWidth / 2 - COLUMN_PADDING_X / 2 - firstColumnStartX;
  const secondColumnStartY = initialY;
  const secondColumnHeight = 70;
  const secondColumnStartX = pageWidth / 2 + COLUMN_PADDING_X / 2;
  const secondColumnWidth = firstColumnWidth;
  const secondColumnSecondPartStartY = secondColumnStartY + secondColumnHeight + 10;
  const secondColumnSecondPartHeight = 90;
  const ITEM_PER_PAGE = 11;

  let totalPages = 1;
  [groupsOfCourses, groupsOfLessons, groupsOfTopics].forEach((group) => {
    const allItems = group.flat();
    totalPages += Math.ceil(allItems.length / (ITEM_PER_PAGE - 1));
  });

  // added fonts
  doc.addFont("Inter.ttf", "Inter", "normal");
  doc.addFont("InterRegular.ttf", "Inter", "regular");
  doc.addFont("InterBold.ttf", "Inter", "bold");

  const addTitle = (doc: jsPDF, title: string, column: number, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#686f84");
    const x = (column === 1 ? firstColumnInternalX : secondColumnStartX) + COLUMN_PADDING_X;
    doc.text(title, x, y);
    return y + 14;
  };

  const addFieldTitle = (doc: jsPDF, title: string, column: number, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(9);
    doc.setTextColor("#81858b");
    const x = (column === 1 ? firstColumnInternalX : secondColumnStartX) + COLUMN_PADDING_X;
    doc.text(title, x, y);
    return y + 12;
  };

  const addFieldValue = (doc: jsPDF, value: string, column: number, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#010237");
    const x = (column === 1 ? firstColumnInternalX : secondColumnStartX) + COLUMN_PADDING_X;
    doc.text(value ?? '', x, y);
    return y + 16;
  };

  const addField = (doc: jsPDF, title: string, value: string, column: number, y: number) => {
    y = addFieldTitle(doc, title, column, y);
    return addFieldValue(doc, value, column, y);
  }

  const addDivider = (doc: jsPDF, column: number, y: number) => {
    doc.setLineWidth(1);
    const linePadding = 20;
    doc.setDrawColor('#E5E5E5');
    if (column === 3) {
      const x1 = firstColumnInternalX + COLUMN_PADDING_X;
      const x2 = firstColumnInternalX + 2 * firstColumnWidth - COLUMN_PADDING_X;
      doc.line(x1, y, x2, y);
      return y + linePadding;
    }
    const x1 = (column === 1 ? firstColumnInternalX : secondColumnStartX) + COLUMN_PADDING_X;
    const x2 = (column === 1 ? firstColumnWidth : secondColumnWidth) + COLUMN_PADDING_X;
    doc.line(x1, y, x2, y);
    return y + linePadding;
  }
  doc.setFillColor("#F7F8FA");

  const addHeader = (doc: jsPDF, page: number, short = false) => {
    doc.setFillColor("#F7F8FA");
    doc.rect(
      0,
      0,
      pageWidth,
      short ? 30 : 55,
      "F"
    );
    drawImage('platform-logo', doc, PAGE_PADDING, 10);
    if (!short) {
      drawImageCircular('user-image', doc, firstColumnStartX, 30);
    }
    doc.setFont("Inter", "bold");
    doc.setFontSize(12);
    doc.setTextColor("#81858b");
    doc.text(`Ficha de estudiante: ${page}/${totalPages}`, pageWidth - PAGE_PADDING, 20, { align: "right" });
  };
  addHeader(doc, 1);

  doc.setFillColor("#F7F8FA");
  doc.roundedRect(
    firstColumnStartX,
    firstColumnStartY,
    firstColumnWidth,
    firstColumnHeight,
    ROUNDED_COLUMN,
    ROUNDED_COLUMN,
    "F"
  );
  doc.roundedRect(
    secondColumnStartX,
    secondColumnStartY,
    secondColumnWidth,
    secondColumnHeight,
    ROUNDED_COLUMN,
    ROUNDED_COLUMN,
    "F"
  );
  doc.roundedRect(
    secondColumnStartX,
    secondColumnSecondPartStartY,
    secondColumnWidth,
    secondColumnSecondPartHeight,
    ROUNDED_COLUMN,
    ROUNDED_COLUMN,
    "F"
  );


  doc.setFont("Inter", "normal");
  doc.setTextColor("#81858B");
  doc.setFontSize(12);

  const addFirstColumnContent = (doc: jsPDF) => {
    let y = firstColumnStartY + COLUMN_PADDING_Y;
    y = addTitle(doc, getTranslations('USER_PROFILE'), 1, y);
    y = addField(
      doc,
      getTranslations('COMPANY_CONTACT_NAMES'),
      (user.given_name ? user.given_name : "") +
      " " +
      (user.middle_name ? user.middle_name : ""),
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('COMPANY_CONTACT_LAST_NAME'),
      (user.family_name ? user.family_name : "") +
      " " +
      (user.secondLastname ? user.secondLastname : ""),
      1,
      y
    );
    y = addDivider(doc, 1, y);
    y = addField(
      doc,
      getTranslations('USERS_EMAIL'),
      user.email,
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_NATIONAL_ID_TYPE'),
      idLabelsByType[user.nationalIdType as IdTypes],
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_NATIONAL_ID'),
      user.nationalId,
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_CELLPHONE'),
      user.phone_number,
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_GENDER'),
      GenderDict()[user.gender],
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_BIRTH_DATE'),
      moment.utc(user.birthdate).format("DD-MM-YYYY"),
      1,
      y
    );
    y = addField(
      doc,
      getTranslations('USERS_COUNTRY'),
      getCountryName(user.country) || "---",
      1,
      y
    );
    addField(
      doc,
      getTranslations('USERS_CITY'),
      user.city,
      1,
      y
    );
  };
  const addSecondColumnContent = (doc: jsPDF) => {
    let y = secondColumnStartY + COLUMN_PADDING_Y;
    y = addField(
      doc,
      getTranslations('USERS_ADDRESS'),
      user.address,
      2,
      y
    );
    addField(
      doc,
      getTranslations('USER_CREATION_DATE'),
      moment.utc(user.createdAt).format("DD-MM-YYYY"),
      2,
      y
    );
  };
  const addSecondColumnSecondPartContent = (doc: jsPDF) => {
    let y = secondColumnSecondPartStartY + COLUMN_PADDING_Y;
    y = addTitle(doc, getTranslations('USER_COMPANY_INFO'), 2, y);
    y = addField(
      doc,
      getTranslations('USERS_JOB'),
      user.position || " ",
      2,
      y
    );
    addField(
      doc,
      getTranslations('USER_COMPANY'),
      company.name,
      2,
      y
    );
  };

  addFirstColumnContent(doc);
  addSecondColumnContent(doc);
  addSecondColumnSecondPartContent(doc);




  const addItemTitle = (doc: jsPDF, title: string, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(8);
    doc.setTextColor("#010237");
    const x = firstColumnInternalX;
    doc.text(title, x, y);
    return y + 12;
  };

  const addItemDescription = (doc: jsPDF, categories: string, date: string | Date, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(7);
    doc.setTextColor("#81858b");
    const x = firstColumnInternalX;
    doc.text(categories, x, y);
    const x2 = secondColumnStartX + firstColumnWidth - COLUMN_PADDING_X;
    const dateText = `Fecha: ${moment.utc(date).format("DD-MM-YYYY")}`;
    doc.text(dateText, x2, y, { align: "right" });
    return y + 16;
  };

  const addWideColumnTitle = (doc: jsPDF, title: string, y: number) => {
    doc.setFont("Inter", "normal");
    doc.setFontSize(10);
    doc.setTextColor("#4f4f4f");
    doc.text(title, firstColumnInternalX, y);
    return y + 18;
  };
  const wideColumnStartY = 40
  let pageNumber = 1;
  const addLearningGroups = (groups: IEvalWithCategory[][], LearningTitle: string) => {
    const allItems = groups.flat();
    const splittedItems: IEvalWithCategory[][] = [];
    let index = 0;
    for (let i = 1; i <= allItems.length; ++i) {
      if (i % ITEM_PER_PAGE === 0) {
        ++index;
      }
      if (splittedItems[index] === undefined) {
        splittedItems[index] = [];
      }
      splittedItems[index].push(allItems[i - 1]);
    }
    splittedItems.forEach((items) => {
      const height = items.length * 40 + 24 + ((items.length - 1) * 8);
      doc.addPage();
      addHeader(doc, ++pageNumber, true);
      doc.setFillColor("#F7F8FA");
      doc.roundedRect(
        firstColumnStartX,
        wideColumnStartY,
        (firstColumnWidth) * 2 + firstColumnStartX / 2,
        height,
        ROUNDED_COLUMN,
        ROUNDED_COLUMN,
        "F"
      );
      let y = wideColumnStartY + COLUMN_PADDING_Y;
      y = addWideColumnTitle(doc, LearningTitle, y);
      items.forEach((item, index) => {
        let title = item.course ?? "";
        if (item.lesson) {
          title = item.lesson;
        } else if (item.topic) {
          title = item.topic;
        }
        y = addItemTitle(doc, title, y);
        y = addItemDescription(doc, item.categoryName, item.finishedAt, y);
        if (index < items.length - 1) {
          y = addDivider(doc, 3, y);
        }
      });
    });
  };
  addLearningGroups(groupsOfCourses, 'Cursos culminados');
  addLearningGroups(groupsOfLessons, 'Lecciones culminadas');
  addLearningGroups(groupsOfTopics, 'Temas culminados');



  doc.save(`user-profile.pdf`);
};
